/* The live implementation of `CockpitTransport`, over the TMF API.
 *
 * This file is the only thing in the app that knows the API exists. Above it,
 * `queries.ts` and every screen deal in `CaseRecord`s and `Offering`s; below it,
 * `client.ts` deals in requests and `map/*.ts` in shapes. What lives here is the
 * *joining* — the part neither of the other two can do alone.
 *
 * Three things it does that are worth reading before changing anything.
 *
 * **It resolves references to identifiers.** `CaseRecord.id` is the human case
 * reference — `CLC-104882` — because that is what appears in the address bar, in
 * a ticket, and in what somebody says out loud. The API is addressed by uuid.
 * The case list carries both, so one read builds the index and every subsequent
 * call translates through it rather than adding a lookup request.
 *
 * **It coalesces the shared reads.** Six screens need the customer list and four
 * need the catalog. Fetching them per method would turn one page load into
 * twenty requests for five distinct answers. A short window — long enough for a
 * burst of mounts, far too short to be a cache — collapses those into one
 * request each. Staleness is still TanStack Query's business, not this file's.
 *
 * **It never invents an identifier.** Where a join fails — a case whose customer
 * is not in the index, a gap on a characteristic the catalog does not have — the
 * raw id is carried through to the screen. It reads badly on purpose: an id is
 * findable, and a placeholder name is a dead end.
 */

import type { components } from '@/shared/api/generated/openapi';
import { z } from 'zod';
import { ApiError, get, getCollection, request } from '@/shared/api/http/client';
import { toCharacteristics, toOfferings } from '@/shared/api/http/map/catalog';
import { toCaseRecord, toCaseRecords, type CaseContext, type WireCase } from '@/shared/api/http/map/cases';
import { toMails, toThreads } from '@/shared/api/http/map/comms';
import {
  toBusinessRules,
  toClassificationRules,
  toCockpitSettings,
  toCohort,
  toTemplates,
  toThresholds,
  templatesByTenant,
} from '@/shared/api/http/map/config';
import {
  toCustomers,
  toRequesterMaster,
  toRequesterMasters,
  toSite,
  toSites,
} from '@/shared/api/http/map/customers';
import { collectEvidence, toDossier, type DossierSources } from '@/shared/api/http/map/dossier';
import {
  toMriFieldSet,
  toWireMriFieldCreate,
  toWireMriFieldSetCreate,
  toWireMriFieldSetPatch,
} from '@/shared/api/http/map/mriAdmin';
import type {
  CaseDossier,
  CaseServiceMapping,
  CatalogCharacteristicDraft,
  CatalogLifecycle,
  CatalogValueType,
  CatalogOfferingAdmin,
  CatalogOfferingDraft,
  CatalogOfferingPatch,
  CatalogCsvImportRowError,
  ClarificationDraftRequest,
  CockpitTransport,
  CsvImportResult,
  GrantQuery,
  ListQuery,
  PageOf,
  RequesterDraft,
  RequesterStatus,
  SavedClarification,
  SignOffRecord,
  SignOffRequest,
  SiteDraft,
  SitePatch,
  TakeoverRequest,
  TenantCustomerDraft,
  TenantCustomerPatch,
  TenantGroupPatch,
  TenantGroupQuery,
} from '@/shared/api/transport';
import type { CohortMeasurements } from '@/shared/domain/analytics';
import type { CaseRecord, EvidenceItem } from '@/shared/domain/case';
import type {
  BusinessRule,
  ClarificationTemplate,
  ClassificationRule,
  QuestionBank,
  Threshold,
} from '@/shared/domain/configuration';
import type { ThreadMessage } from '@/shared/domain/conversation';
import type { Customer, RequesterMaster, Site } from '@/shared/domain/customer';
import type { SessionUser } from '@/shared/domain/identity';
import type { CaseEmail, CaseEmailContent, EmailImportOutcome, EmailImportResult, EmailMailbox, EmailMessagePreview, MailRecord } from '@/shared/domain/intake';
import type {
  MriFieldDraft,
  MriFieldSetAdmin,
  MriFieldSetDraft,
  MriFieldSetKind,
  MriFieldSetPatch,
} from '@/shared/domain/mriAdmin';import type { Characteristic, Offering } from '@/shared/domain/serviceCatalog';
import type { CockpitSettings } from '@/shared/domain/settings';
import type {
  AccessGrant,
  TenantChoices,
  TenantCustomer,
  TenantCustomerMasterData,
  TenantGroup,
  TenantGroupAssignment,
  TenantGroupServiceCandidate,
} from '@/shared/domain/tenancy';

/* ---------- paths ---------- */

const CASES = '/tmf-api/clarificationCaseManagement/v1';
const CATALOG_ADMIN = '/catalog-admin/v1';
const CUSTOMERS = '/tmf-api/customerManagement/v1';
const REQUESTERS = '/tmf-api/requesterManagement/v1';
const CONFIG_ADMIN = '/config-admin/v1';
const ORDERS = '/tmf-api/productOrderingManagement/v1';
const COCKPIT = '/cockpit/v1';

/* Not `/tmf-api/`, on purpose. TMF632 models a party and TMF645 a service
   qualification; neither describes "which customers may this signed-in person
   work on", which is a fact about this deployment rather than about a resource
   anybody integrates against. Same reasoning that puts `/cockpit/v1` outside the
   standard namespace. */
const TENANCY = '/tenancy/v1';

/** The server clamps to its own maximum; asking for more than it will give is
    how a caller says "as many as I can have" without needing to know the cap. */
const PAGE = 500;

/* ---------- wire aliases ---------- */

type Schemas = components['schemas'];
type WireCharacteristic = Schemas['ServiceSpecCharacteristicModel'];
type WireSpecification = Schemas['ServiceSpecificationModel'];
type WireCandidate = Schemas['ServiceCandidateModel'];
type WireCustomer = Schemas['CustomerModel'];
type WireSite = Schemas['GeographicSiteModel'];
type WireSiteCreate = Schemas['GeographicSiteCreate'];
type WireSitePatch = Schemas['GeographicSitePatch'];
type WireSiteBulkCreate = Schemas['GeographicSiteBulkCreate'];
type WireRequester = Schemas['RequesterModel'];
type WireRequesterCreate = Schemas['RequesterCreate'];
type WireRequesterStatusPatch = Schemas['RequesterStatusPatch'];
type WireGap = Schemas['GapModel'];
type WireServiceMapping = Schemas['CaseServiceMappingModel'];
type WireValue = Schemas['CharacteristicValueModel'];
type WireConflict = Schemas['ConflictModel'];
type WireCaseItem = Schemas['CaseItemModel'];
type WireEmailImportResult = Schemas['EmailImportResultModel'];
type WireEmailPreview = Schemas['EmailMessagePreview'];
type WireMailbox = Schemas['MailboxModel'];
type WireClarification = Schemas['ClarificationModel'];
type WireClarificationCreate = Schemas['ClarificationCreate'];
type WireOrder = Schemas['ProductOrderModel'];
type WireRule = Schemas['BusinessRuleModel'];
type WireThreshold = Schemas['RoutingThresholdModel'];
type WireTemplate = Schemas['ClarificationTemplateModel'];
type WireSettings = Schemas['CockpitSettingsModel'];
type WireCohort = Schemas['CohortModel'];
type WireSession = Schemas['SessionModel'];
type WireTenantChoices = Schemas['TenantChoicesModel'];
type WireTenantCustomer = Schemas['CustomerSummaryModel'];
type WireTenantCustomerMasterData = Schemas['CustomerMasterDataModel'];
type WireCustomerCreate = Schemas['CustomerCreate'];
type WireCustomerPatch = Schemas['CustomerPatch'];
type WireCustomerBulkCreate = Schemas['CustomerBulkCreate'];
type WireCatalogOffering = Schemas['CatalogOfferingAdminModel'];
type WireCatalogOfferingCreate = Schemas['CatalogOfferingCreate'];
type WireCatalogOfferingPatch = Schemas['CatalogOfferingPatch'];
type WireCatalogCharacteristicCreate = Schemas['CatalogCharacteristicCreate'];
type WireCsvImportResult = Schemas['CsvImportResultModel'];
type WireCatalogCsvImportRowError = Schemas['CatalogCsvImportRowError'];
type WireGrant = Schemas['GrantModel'];
type WireGrantCreate = Schemas['GrantCreate'];
type WireTenantGroup = Schemas['TenantGroupModel'];
type WireTenantGroupCreate = Schemas['TenantGroupCreate'];
type WireTenantGroupPatch = Schemas['TenantGroupPatch'];
type WireCustomerAssignments = Schemas['CustomerAssignments'];
type WireServiceAssignments = Schemas['ServiceAssignments'];
type WireMriFieldSet = Schemas['MriFieldSetModel'];
type WireTenantCustomerAssignment = Schemas['TenantCustomerAssignmentModel'];
type WireTenantServiceAssignment = Schemas['TenantServiceAssignmentModel'];
type WireTenantGroupServiceCandidate = Schemas['ServiceCandidateSelectionModel'];

const EMAIL_IMPORT_OUTCOMES: ReadonlySet<string> = new Set([
  'created',
  'replayed',
  'duplicate',
  'quarantined',
]);

function toEmailMailbox(wire: WireMailbox): EmailMailbox {
  return {
    address: wire.address,
    displayName: wire.displayName ?? null,
  };
}

function toEmailPreview(wire: WireEmailPreview): EmailMessagePreview {
  return {
    messageId: wire.messageId ?? null,
    subject: wire.subject ?? null,
    sender: wire.sender ? toEmailMailbox(wire.sender) : null,
    to: (wire.to ?? []).map(toEmailMailbox),
    cc: (wire.cc ?? []).map(toEmailMailbox),
    sentAt: wire.sentAt ?? null,
    receivedAt: wire.receivedAt ?? null,
    isAutoReply: wire.isAutoReply,
    attachmentCount: wire.attachmentCount,
    bodyRetained: wire.bodyRetained,
    defects: wire.defects ?? [],
  };
}

function toEmailImportResult(wire: WireEmailImportResult): EmailImportResult {
  if (!EMAIL_IMPORT_OUTCOMES.has(wire.outcome)) {
    throw new ApiError({
      status: 502,
      code: 'invalidEmailImportOutcome',
      reason: 'The email import response has an unknown outcome',
      message: `The service returned the unsupported email import outcome "${wire.outcome}".`,
      correlationId: null,
    });
  }

  return {
    outcome: wire.outcome as EmailImportOutcome,
    caseItemId: wire.caseItem?.id ?? null,
    sourceId: wire.caseItem?.sourceId ?? wire.message?.messageId ?? null,
    duplicateOfId: wire.duplicateOf?.id ?? null,
    quarantineReason: wire.quarantine?.reason ?? null,
    attachmentCount: wire.attachment?.length ?? wire.message?.attachmentCount ?? 0,
    message: wire.message ? toEmailPreview(wire.message) : null,
  };
}

function toCsvImportResult(wire: WireCsvImportResult): CsvImportResult {
  return {
    created: wire.created,
    total: wire.total,
    errors: (wire.errors ?? []).map(toCatalogCsvImportRowError),
  };
}

function toCatalogCsvImportRowError(wire: WireCatalogCsvImportRowError): CatalogCsvImportRowError {
  return {
    row: wire.row,
    code: wire.code,
    message: wire.message,
    values: wire.values ?? {},
  };
}

/* ---------- tenancy shapes ---------- */

/* Two mappers rather than inline object literals, because each is used from two
   methods and the fields they rename are the ones a typo would be silent in:
   `resourceVersion` becomes `version` because that is what every other row in
   this cockpit calls the number it sends back as `If-Match`, and `grantCount`
   becomes `grants` because the column heading is "Operators", not "Grant
   count". */

function toTenantCustomer(wire: WireTenantCustomer): TenantCustomer {
  return {
    id: wire.id,
    name: wire.name,
    status: wire.status,
    countryCode: wire.countryCode ?? null,
    defaultLanguage: wire.defaultLanguage ?? null,
    grants: wire.grantCount,
    version: wire.resourceVersion,
  };
}

function toCatalogOffering(wire: WireCatalogOffering): CatalogOfferingAdmin {
  return {
    id: wire.id,
    candidateName: wire.candidateName,
    category: wire.category ?? undefined,
    lifecycleStatus: catalogLifecycle(wire.lifecycleStatus),
    resourceVersion: wire.resourceVersion,
    catalogId: wire.catalogId,
    catalogName: wire.catalogName,
    catalogVersion: wire.catalogVersion,
    catalogLifecycle: catalogLifecycle(wire.catalogLifecycle),
    catalogResourceVersion: wire.catalogResourceVersion,
    specificationId: wire.specificationId,
    specificationName: wire.specificationName,
    specificationVersion: wire.specificationVersion,
    specificationDescription: wire.specificationDescription ?? undefined,
    specificationLifecycle: catalogLifecycle(wire.specificationLifecycle),
    specificationActivatedAt: wire.specificationActivatedAt ?? null,
    specificationResourceVersion: wire.specificationResourceVersion,
    isBundle: wire.isBundle,
    characteristics: wire.characteristics.map((characteristic) => ({
      name: characteristic.name,
      ...(characteristic.businessName ? { businessName: characteristic.businessName } : {}),
      valueType: catalogValueType(characteristic.valueType),
      minCardinality: characteristic.minCardinality,
      ...(characteristic.maxCardinality === null
        ? {}
        : { maxCardinality: characteristic.maxCardinality }),
      mriDimension: catalogDimension(characteristic.mriDimension),
      ...(characteristic.confidenceFloor === null
        ? {}
        : { confidenceFloor: Number(characteristic.confidenceFloor) }),
      ...(characteristic.conditionalRuleKey
        ? { conditionalRuleKey: characteristic.conditionalRuleKey }
        : {}),
      ...(characteristic.validationRuleKey
        ? { validationRuleKey: characteristic.validationRuleKey }
        : {}),
      ...(characteristic.displayOrder === null
        ? {}
        : { displayOrder: characteristic.displayOrder }),
      values: characteristic.values.map((value) => ({
        value: value.value,
        ...(value.isDefault ? { isDefault: true } : {}),
      })),
    })),
    regions: wire.regions ?? [],
  };
}

function toWireCatalogCharacteristic(
  draft: CatalogCharacteristicDraft,
): WireCatalogCharacteristicCreate {
  return {
    name: draft.name,
    valueType: draft.valueType,
    minCardinality: draft.minCardinality,
    maxCardinality: draft.maxCardinality ?? 1,
    mriDimension: draft.mriDimension,
    ...(draft.businessName ? { businessName: draft.businessName } : {}),
    ...(draft.confidenceFloor === undefined ? {} : { confidenceFloor: draft.confidenceFloor }),
    ...(draft.conditionalRuleKey ? { conditionalRuleKey: draft.conditionalRuleKey } : {}),
    ...(draft.validationRuleKey ? { validationRuleKey: draft.validationRuleKey } : {}),
    ...(draft.displayOrder === undefined ? {} : { displayOrder: draft.displayOrder }),
    values: draft.values.map((value) => ({
      value: value.value,
      isDefault: value.isDefault ?? false,
    })),
  };
}

function toWireCatalogOffering(draft: CatalogOfferingDraft): WireCatalogOfferingCreate {
  return {
    catalogName: draft.catalogName,
    catalogVersion: draft.catalogVersion,
    catalogLifecycle: draft.catalogLifecycle ?? 'In study',
    specificationName: draft.specificationName,
    specificationVersion: draft.specificationVersion,
    ...(draft.specificationDescription === undefined
      ? {}
      : { specificationDescription: draft.specificationDescription }),
    isBundle: draft.isBundle ?? false,
    candidateName: draft.candidateName,
    ...(draft.category === undefined ? {} : { category: draft.category }),
    regions: [...draft.regions],
    lifecycleStatus: draft.lifecycleStatus ?? 'In study',
    characteristics: draft.characteristics.map(toWireCatalogCharacteristic),
  };
}

const CATALOG_VALUE_TYPES: readonly CatalogValueType[] = [
  'String',
  'Integer',
  'Decimal',
  'Boolean',
  'Date',
  'DateTime',
  'GeographicSiteRef',
  'CustomerRef',
  'RequesterRef',
];
const CATALOG_DIMENSIONS: readonly CatalogCharacteristicDraft['mriDimension'][] = [
  'Who',
  'What',
  'Why',
  'Where',
  'When',
  'How',
];
const CATALOG_LIFECYCLES: readonly CatalogLifecycle[] = [
  'In study',
  'In design',
  'In test',
  'Launched',
  'Retired',
  'Obsolete',
];

function catalogValueType(value: string): CatalogValueType {
  return CATALOG_VALUE_TYPES.find((candidate) => candidate === value) ?? 'String';
}

function catalogDimension(value: string): CatalogCharacteristicDraft['mriDimension'] {
  return CATALOG_DIMENSIONS.find((candidate) => candidate === value) ?? 'What';
}

function catalogLifecycle(value: string): CatalogLifecycle {
  return CATALOG_LIFECYCLES.find((candidate) => candidate === value) ?? 'In study';
}

function toAccessGrant(wire: WireGrant): AccessGrant {
  return {
    id: wire.id,
    subject: wire.subject,
    customerId: wire.customerId,
    customerName: wire.customerName ?? null,
    live: wire.live,
    grantedAt: wire.grantedAt,
    grantedBy: wire.grantedBy,
    revokedAt: wire.revokedAt ?? null,
    revokedBy: wire.revokedBy ?? null,
  };
}

function toTenantGroup(wire: WireTenantGroup): TenantGroup {
  const lifecycle = wire.lifecycleStatus === 'retired' ? 'retired' : 'active';
  return {
    id: wire.id,
    name: wire.name,
    lifecycle,
    customerCount: wire.customerCount,
    serviceCount: wire.serviceCount,
    version: wire.resourceVersion,
  };
}

function toTenantGroupAssignment(
  wire: WireTenantCustomerAssignment | WireTenantServiceAssignment,
): TenantGroupAssignment {
  return { id: wire.id, name: wire.name };
}

function toTenantGroupServiceCandidate(
  wire: WireTenantGroupServiceCandidate,
): TenantGroupServiceCandidate {
  return { id: wire.id, name: wire.name };
}

/** One page of grants, for whichever of the two selectors the caller holds.
 *
 * `/grants` insists on exactly one of `customerId` and `subject` — the service
 * says why — so the selector arrives already narrowed to one and the rest of the
 * query is the same either way. `search` is a substring of the subject and is a
 * different parameter from `subject`, which is exact; sending both would be
 * asking for one identity whose name also contains something else, which is not
 * a question either screen asks. */
async function grantPage(
  selector: { readonly customerId: string } | { readonly subject: string },
  query: GrantQuery,
): Promise<PageOf<AccessGrant>> {
  const page = await request<WireGrant[]>('GET', `${TENANCY}/grants`, {
    query: {
      ...selector,
      search: query.search,
      live: query.live,
      offset: query.offset,
      limit: query.limit,
    },
  });
  const rows = page.data.map(toAccessGrant);
  return { rows, total: page.total ?? rows.length };
}

/* ---------- request coalescing ---------- */

/** How long an in-flight read is shared with a caller asking for the same thing.
 *
 * Five seconds is chosen against one specific event: the cockpit mounting. The
 * sidebar, the queue and the rail all ask for the case list within a few
 * hundred milliseconds of each other, and without this that is three identical
 * requests. It is deliberately far shorter than any `staleTime` in
 * `queries.ts` — this is a de-duplicator, not a second cache sitting under the
 * first one where nobody would think to look for stale data. */
const COALESCE_MS = 5_000;

const inflight = new Map<string, { readonly at: number; readonly value: Promise<unknown> }>();

function coalesce<T>(key: string, load: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const existing = inflight.get(key);
  if (existing && now - existing.at < COALESCE_MS) {
    return existing.value as Promise<T>;
  }

  const value = load();
  inflight.set(key, { at: now, value });
  // A failure is not shared. Holding a rejection for five seconds would make a
  // retry — the user pressing reload — return the same error without asking the
  // service whether it had recovered.
  void value.catch(() => {
    if (inflight.get(key)?.value === value) inflight.delete(key);
  });
  return value;
}

/* ---------- the shared reads ---------- */

/** The case list, with both ways of naming a case.
 *
 * `idOf` is what turns a route parameter back into an API call, and it is the
 * reason the case list is fetched before any single-case read: there is no
 * endpoint that takes a reference, and adding one would be a second address for
 * a resource that already has one. */
interface CaseIndex {
  readonly wires: readonly WireCase[];
  readonly byId: ReadonlyMap<string, WireCase>;
  readonly idOf: ReadonlyMap<string, string>;
  readonly referenceOf: ReadonlyMap<string, string>;
}

async function loadCases(): Promise<CaseIndex> {
  return coalesce('cases', async () => {
    const wires = await getCollection<WireCase>(`${CASES}/clarificationCase`, {
      query: { limit: PAGE },
    });
    const byId = new Map<string, WireCase>();
    const idOf = new Map<string, string>();
    const referenceOf = new Map<string, string>();
    for (const wire of wires) {
      byId.set(wire.id, wire);
      idOf.set(wire.caseReference, wire.id);
      referenceOf.set(wire.id, wire.caseReference);
    }
    return { wires, byId, idOf, referenceOf };
  });
}

/** Every characteristic in the catalog, indexed by id.
 *
 * Two indexes from one read, because the two consumers key differently: the
 * cockpit's catalogue is a vocabulary keyed by name, and values, gaps and
 * conflicts all reference a characteristic by id. Deriving both from one list is
 * what stops them disagreeing. */
interface CharacteristicIndex {
  readonly wires: readonly WireCharacteristic[];
  readonly byId: ReadonlyMap<string, WireCharacteristic>;
}

async function loadCharacteristics(): Promise<CharacteristicIndex> {
  return coalesce('characteristics', async () => {
    const wires = await getCollection<WireCharacteristic>(`${CATALOG_ADMIN}/serviceSpecCharacteristic`, {
      query: { limit: PAGE },
    });
    return { wires, byId: new Map(wires.map((wire) => [wire.id, wire])) };
  });
}

async function loadCandidates(): Promise<readonly WireCandidate[]> {
  return coalesce('candidates', () =>
    getCollection<WireCandidate>(`${CATALOG_ADMIN}/serviceCandidate`, { query: { limit: PAGE } }),
  );
}

async function loadSpecifications(): Promise<readonly WireSpecification[]> {
  return coalesce('specifications', () =>
    getCollection<WireSpecification>(`${CATALOG_ADMIN}/serviceSpecification`, { query: { limit: PAGE } }),
  );
}

async function loadCustomers(): Promise<readonly WireCustomer[]> {
  return coalesce('customers', () =>
    getCollection<WireCustomer>(`${CUSTOMERS}/customer`, { query: { limit: PAGE } }),
  );
}

async function loadTemplates(): Promise<readonly WireTemplate[]> {
  return coalesce('templates', () =>
    getCollection<WireTemplate>(`${CONFIG_ADMIN}/clarificationTemplate`, { query: { limit: PAGE } }),
  );
}

/** The rules in force. Both admin tables are drawn from this one list — one
    takes the classification rules and the other takes everything else — so they
    cannot be reporting two different configuration versions. */
async function loadRules(): Promise<readonly WireRule[]> {
  return coalesce('rules', () =>
    getCollection<WireRule>(`${CONFIG_ADMIN}/businessRule`, { query: { status: 'active', limit: PAGE } }),
  );
}

async function loadThresholds(): Promise<readonly WireThreshold[]> {
  return coalesce('thresholds', () =>
    getCollection<WireThreshold>(`${CONFIG_ADMIN}/routingThreshold`, {
      query: { status: 'active', limit: PAGE },
    }),
  );
}

/** The one threshold version being applied to this caller's cases.
 *
 * A tenant-scoped version overrides the global one — that is what scoping a
 * configuration version to a customer means — so where both are active the
 * customer's is the one in force, and it is the only one the settings screen
 * should show. Showing both would show a set of floors that is not being
 * applied to anything, beside the set that is. */
function activeThreshold(wires: readonly WireThreshold[]): WireThreshold | null {
  return wires.find((wire) => wire.customerId) ?? wires[0] ?? null;
}

/** Open gaps across the tenant, in one read.
 *
 * The alternative is one request per case for a column, which is what the
 * tenant-wide `/gap` collection exists to avoid. Every row links back to its own
 * case, so grouping is free. */
async function loadOpenGaps(): Promise<readonly WireGap[]> {
  return coalesce('gaps', () =>
    getCollection<WireGap>(`${CASES}/gap`, { query: { status: 'open', limit: PAGE } }),
  );
}

/** Case id → the order reference it projects to.
 *
 * Only signed-off cases appear: an order is computed from a complete case and
 * does not exist before sign-off. A case with no entry has no order reference,
 * which is what the queue's empty cell means. */
async function loadOrderRefs(): Promise<ReadonlyMap<string, string>> {
  return coalesce('orders', async () => {
    const wires = await getCollection<WireOrder>(`${ORDERS}/productOrder`, {
      query: { limit: PAGE },
    });
    return new Map(wires.map((wire) => [wire.case?.id ?? wire.id, wire.externalId]));
  });
}

/* ---------- the joins ---------- */

/** Customer id → display name, for every screen that shows a tenant. */
async function customerNames(): Promise<ReadonlyMap<string, string>> {
  const wires = await loadCustomers();
  return new Map(wires.map((wire) => [wire.id, wire.name]));
}

/** Service-candidate id → the offering name a person would recognise. */
async function offeringNames(): Promise<ReadonlyMap<string, string>> {
  const wires = await loadCandidates();
  return new Map(wires.map((wire) => [wire.id, wire.name]));
}

/** Open gaps, grouped two ways from one read.
 *
 * The queue wants a count per case and the gap map wants the characteristic
 * names per case, and both must agree — a row saying "3 gaps" beside a panel
 * listing two of them is the kind of discrepancy that costs an afternoon. They
 * are the same list, counted and named. */
interface GapIndex {
  /** Case uuid → how many gaps are open on it. */
  readonly counts: ReadonlyMap<string, number>;
  /** Case reference → the characteristics behind them, in the API's order. */
  readonly names: Readonly<Record<string, readonly string[]>>;
}

/** What a gap points at when it points at nothing nameable. */
const DASH_NAME = 'unknown characteristic';

async function gapIndex(): Promise<GapIndex> {
  const [gaps, characteristics, cases] = await Promise.all([
    loadOpenGaps(),
    loadCharacteristics(),
    loadCases(),
  ]);

  const counts = new Map<string, number>();
  const names: Record<string, string[]> = {};

  for (const gap of gaps) {
    const caseId = gap.case?.id;
    if (!caseId) continue;
    counts.set(caseId, (counts.get(caseId) ?? 0) + 1);

    const reference = cases.referenceOf.get(caseId);
    if (!reference) continue;
    // The id where the catalog has no such characteristic. A gap on a field
    // nobody can name is a real inconsistency between the two, and printing the
    // id is what lets somebody go and find which of them is wrong.
    const name = gap.characteristic?.id
      ? (characteristics.byId.get(gap.characteristic.id)?.name ?? gap.characteristic.id)
      : DASH_NAME;
    const list = names[reference] ?? [];
    if (!list.includes(name)) list.push(name);
    names[reference] = list;
  }

  return { counts, names };
}

/** Everything a case row needs beyond its own fields.
 *
 * Four reads, in parallel, shared with every other method through the coalescer.
 * A screen showing only the queue pays for four requests once; a screen showing
 * the queue and the catalog pays for the same four. */
async function caseContext(): Promise<CaseContext> {
  const [customers, offerings, orderRefs, gaps] = await Promise.all([
    customerNames(),
    offeringNames(),
    loadOrderRefs(),
    gapIndex(),
  ]);
  return {
    customerNames: customers,
    offeringNames: offerings,
    orderRefs,
    openGapCounts: gaps.counts,
    now: new Date(),
  };
}

/* ---------- the evidence register ---------- */

/** Cited source → the span read from it, accumulated as dossiers are opened.
 *
 * There is no endpoint behind `getEvidence()`, and that is deliberate rather
 * than missing: provenance belongs to one case's values, and a tenant-wide
 * collection of it would mean reading every case's values to answer a question
 * about the one on screen. So the register fills as cases are opened, and the
 * evidence panel can cite what the reader has actually looked at.
 *
 * It only ever grows within a session. Every entry is keyed by a locator that
 * identifies one span of one source, so two cases citing the same mail agree
 * about what it said. */
const register = new Map<string, EvidenceItem>();

/** Dossier reads still in flight, so `getEvidence()` does not answer from a
    register that is about to be filled by the request beside it. */
const gathering = new Set<Promise<unknown>>();

function remember(items: Readonly<Record<string, EvidenceItem>>): void {
  for (const [locator, item] of Object.entries(items)) {
    if (!register.has(locator)) register.set(locator, item);
  }
}

/* ---------- one case ---------- */

/** The five reads behind the case workspace, issued together.
 *
 * The case itself comes back with its state transitions inline — that is what
 * the single-case GET returns without a `?fields=` selector — so the timeline
 * costs no extra request. The other four are separate collections because the
 * API keeps them separate: a value is corrected under its own ETag, a gap closes
 * on its own, and an intake item never changes at all.
 */
async function loadDossierSources(caseId: string): Promise<DossierSources> {
  const [wire, values, conflicts, gaps, items] = await Promise.all([
    get<WireCase>(`${CASES}/clarificationCase/${caseId}`),
    get<WireValue[]>(`${CASES}/clarificationCase/${caseId}/characteristicValue`, {
      query: { limit: PAGE },
    }),
    get<WireConflict[]>(`${CASES}/clarificationCase/${caseId}/conflict`, {
      query: { limit: PAGE },
    }),
    get<WireGap[]>(`${CASES}/clarificationCase/${caseId}/gap`, {
      query: { limit: PAGE },
    }),
    getCollection<WireCaseItem>(`${CASES}/caseItem`, {
      query: { 'case.id': caseId, limit: PAGE },
    }),
  ]);
  return { case: wire, values, conflicts, gaps, items };
}

/** How many cases the conversations screen fetches drafts for.
 *
 * Outbound clarifications are addressed per case — the whole slice is nested
 * under `clarificationCase/{caseId}` so that a draft id from another case is not
 * reachable — and there is no tenant-wide collection of them. So one screen
 * showing every thread is one request per case, and a tenant with a thousand
 * open cases would open a thousand connections to draw a table.
 *
 * The cap is on the *outbound* half only. Every case's inbound correspondence is
 * always shown, because that comes from the flat `caseItem` collection in one
 * read. Beyond the cap a thread shows what arrived and not what was sent, which
 * is a visible partial answer rather than a missing row.
 */
const THREAD_CASES = 40;

/** Drafts per case, for the first `THREAD_CASES` in the list.
 *
 * A failed read for one case yields no drafts for that case rather than failing
 * the screen: forty requests will occasionally include one that does not answer,
 * and losing the whole conversations view to it would be the wrong trade. */
async function loadClarifications(
  caseIds: readonly string[],
): Promise<ReadonlyMap<string, readonly WireClarification[]>> {
  const drafts = new Map<string, readonly WireClarification[]>();
  await Promise.all(
    caseIds.map(async (caseId) => {
      try {
        drafts.set(
          caseId,
          await get<WireClarification[]>(
            `${CASES}/clarificationCase/${caseId}/clarification`,
            { query: { limit: PAGE } },
          ),
        );
      } catch {
        drafts.set(caseId, []);
      }
    }),
  );
  return drafts;
}

/* ---------- the transport ---------- */

export const httpTransport: CockpitTransport = {
  async listCaseEmails(caseReference: string): Promise<readonly CaseEmail[]> {
    const index = await loadCases();
    const caseId = index.idOf.get(caseReference);
    if (!caseId) throw new Error('No such case');
    const items = await getCollection<WireCaseItem>(`${CASES}/caseItem`, {
      query: { 'case.id': caseId, sourceType: 'email', includeDuplicates: true, limit: PAGE },
    });
    return items.map((item) => ({
      id: item.id,
      reference: item.messageCode,
      subject: item.subject ?? null,
      sender: item.senderAddress ?? null,
      receivedAt: item.receivedAt,
      sentAt: item.sentAt ?? null,
      sourceId: item.sourceId,
      duplicateOf: item.duplicateOfId ?? null,
    })).sort((first, second) => first.receivedAt.localeCompare(second.receivedAt));
  },

  async getCaseEmailContent(itemId: string): Promise<CaseEmailContent> {
    const item = await get<WireCaseItem>(`${CASES}/caseItem/${encodeURIComponent(itemId)}`);
    let body: string | null = null;
    try {
      const retained = await get<Schemas['CaseItemBodyModel']>(
        `${CASES}/caseItem/${encodeURIComponent(itemId)}/body`,
      );
      body = retained.bodyText;
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 404) throw error;
    }
    return {
      body,
      attachments: (item.attachment ?? []).map((attachment) => attachment.filename),
      sources: (item.sourceRef ?? []).map((source) => `${source.system}: ${source.externalId}`),
    };
  },

  async listCases(): Promise<readonly CaseRecord[]> {
    const [index, context] = await Promise.all([loadCases(), caseContext()]);
    return toCaseRecords(index.wires, context);
  },

  /** One row, by the reference in the address bar.
   *
   * Answered from the list rather than by a single-case read. The list is
   * already loaded — the sidebar and the queue both asked for it — and the row
   * a screen shows must be the row the queue showed, which two reads a second
   * apart do not guarantee. `null` for a reference that resolves to nothing,
   * which the route renders as "no such case" rather than as an error. */
  async getCase(id: string): Promise<CaseRecord | null> {
    const [index, context] = await Promise.all([loadCases(), caseContext()]);
    const caseId = index.idOf.get(id);
    const wire = caseId ? index.byId.get(caseId) : undefined;
    return wire ? toCaseRecord(wire, context) : null;
  },

  async listMails(): Promise<readonly MailRecord[]> {
    const [items, index, customers, offerings] = await Promise.all([
      getCollection<WireCaseItem>(`${CASES}/caseItem`, { query: { limit: PAGE } }),
      loadCases(),
      customerNames(),
      offeringNames(),
    ]);
    return toMails(items, {
      cases: index.byId,
      customerNames: customers,
      offeringNames: offerings,
    });
  },

  async uploadEmail(file: File): Promise<EmailImportResult> {
    const multipart = new FormData();
    multipart.set('file', file, file.name);
    const imported = await request<WireEmailImportResult>('POST', `${CASES}/emailImport`, {
      multipart,
    });
    return toEmailImportResult(imported.data);
  },

  async reanalyseEmail(caseItemId: string, requestId: string): Promise<void> {
    const body: components['schemas']['EmailReanalyseRequest'] = { requestId };
    const response = await request<unknown>('POST', `${CASES}/caseItem/${encodeURIComponent(caseItemId)}/reanalyse`, { body });
    z.object({ jobId: z.uuid(), caseItemId: z.uuid(), status: z.string() }).parse(response.data);
  },

  /** The signed-in caller, as the API describes them.
   *
   * All nine published fields, and every one of them a token or a name a person
   * supplied — the endpoint no longer answers in English. The mapping is
   * mechanical because there is nothing left to interpret: what these tokens are
   * *called* is decided by `vocabulary.ts` at render time, in the reader's
   * language, and the two optional-in-the-schema list fields are normalised to
   * empty arrays here so no screen has to ask whether "no capabilities" arrived
   * as `[]` or as absent. */
  async getSession(): Promise<SessionUser> {
    const wire = await get<WireSession>(`${COCKPIT}/session`);
    return {
      subject: wire.subject,
      name: wire.name,
      email: wire.email ?? null,
      roles: wire.roles ?? [],
      capabilities: wire.capabilities ?? [],
      structuralLimits: wire.structuralLimits ?? [],
      customerId: wire.customerId,
      customerName: wire.customerName ?? null,
      actorType: wire.actorType,
    };
  },

  async getCohort(params?: { since?: string; until?: string }): Promise<CohortMeasurements> {
    const query: Record<string, string> = {};
    if (params?.since) query['since'] = params.since;
    if (params?.until) query['until'] = params.until;
    const hasQuery = Object.keys(query).length > 0;
    return toCohort(await get<WireCohort>(`${COCKPIT}/cohort`, hasQuery ? { query } : undefined));
  },

  /**
   * The case workspace, from a reference.
   *
   * The reference resolves through the case index, so opening a case from a
   * bookmark costs the list read the cockpit was going to make anyway. A
   * reference that does not resolve raises a 404-shaped `ApiError` rather than
   * an empty dossier — a workspace showing no values, no gaps and no history is
   * indistinguishable from a case that genuinely has none.
   *
   * The evidence register fills as a side effect, and the promise is held in
   * `gathering` until it does, so `getEvidence()` running beside this cannot
   * answer from a register that is one microtask short of complete.
   */
  async getDossier(id: string): Promise<CaseDossier> {
    const index = await loadCases();
    const caseId = index.idOf.get(id);
    if (!caseId) {
      throw new ApiError({
        status: 404,
        code: 'CASE_NOT_FOUND',
        reason: 'No such case',
        message: `No case in this tenant carries the reference ${id}.`,
        correlationId: null,
      });
    }

    const work = (async () => {
      const [sources, fieldSets] = await Promise.all([
        loadDossierSources(caseId),
        get<WireMriFieldSet[]>(`${CONFIG_ADMIN}/mriFieldSet`),
      ]);
      const pinnedSet = fieldSets
        .map(toMriFieldSet)
        .find((fieldSet) => fieldSet.id === sources.case.mriFieldSet?.id);
      const fields = new Map(pinnedSet?.fields.map((field) => [field.id, field]) ?? []);
      remember(collectEvidence(sources.values));
      return toDossier(sources, { fields });
    })();

    gathering.add(work);
    try {
      return await work;
    } finally {
      gathering.delete(work);
    }
  },

  async listCaseServiceMappings(id: string): Promise<readonly CaseServiceMapping[]> {
    const index = await loadCases();
    const caseId = index.idOf.get(id);
    if (!caseId) {
      throw new ApiError({
        status: 404,
        code: 'CASE_NOT_FOUND',
        reason: 'No such case',
        message: `No case in this tenant carries the reference ${id}.`,
        correlationId: null,
      });
    }
    const mappings = await get<WireServiceMapping[]>(
      `${CASES}/clarificationCase/${caseId}/serviceMapping`,
    );
    return mappings.map((mapping) => ({
      id: mapping.id,
      serviceCandidateId: mapping.serviceCandidate?.id ?? null,
      specificationId: mapping.specification?.id ?? null,
      serviceName: mapping.serviceName ?? null,
      serviceDescription: mapping.serviceDescription ?? null,
      isActive: mapping.isActive,
    }));
  },

  async createClarificationDraft(
    caseId: string,
    draft: ClarificationDraftRequest,
  ): Promise<SavedClarification> {
    const body: WireClarificationCreate = {
      subjectRendered: draft.subjectRendered,
      bodyRendered: draft.bodyRendered,
      channel: 'email',
    };
    const created = await request<WireClarification>('POST', `${CASES}/clarificationCase/${caseId}/clarification`, {
      body,
    });
    return {
      id: created.data.id,
      status: created.data.status,
      draftVersion: created.data.resourceVersion,
      subjectRendered: created.data.subjectRendered,
      bodyRendered: created.data.bodyRendered,
    };
  },

  async listOpenGaps(): Promise<Readonly<Record<string, readonly string[]>>> {
    return (await gapIndex()).names;
  },

  async takeOverCase(id: string, body: TakeoverRequest, version: string): Promise<CaseRecord> {
    const [index, context] = await Promise.all([loadCases(), caseContext()]);
    const caseId = index.idOf.get(id);
    if (!caseId) throw new ApiError({ status: 404, code: 'CASE_NOT_FOUND', reason: 'No such case', message: `No case in this tenant carries the reference ${id}.`, correlationId: null });
    const wire = await request<WireCase>('POST', `${CASES}/clarificationCase/${caseId}/takeover`, {
      body,
      ifMatch: version,
    });
    // Bust the coalescer so the React Query re-fetch that follows goes to the
    // server rather than being answered by the 5-second in-flight window, which
    // would return the pre-takeover state and make the status pill look stale.
    inflight.delete('cases');
    return toCaseRecord(wire.data, context);
  },

  async assignCase(
    id: string,
    version: string,
    owner?: string,
  ): Promise<CaseRecord> {
    const [index, context] = await Promise.all([
      loadCases(),
      caseContext(),
    ]);

    const caseId = index.idOf.get(id);

    if (!caseId) {
      throw new ApiError({
        status: 404,
        code: 'CASE_NOT_FOUND',
        reason: 'No such case',
        message: `No case in this tenant carries the reference ${id}.`,
        correlationId: null,
      });
    }

    const wire = await request<WireCase>(
      'POST',
      `${CASES}/clarificationCase/${caseId}/assign`,
      {
        body: owner ? { owner } : {},
        ifMatch: version,
      },
    );

    return toCaseRecord(wire.data, context);
  },

  async signOffCase(id: string, body: SignOffRequest, version: string): Promise<SignOffRecord> {
    const index = await loadCases();
    const caseId = index.idOf.get(id);
    if (!caseId) throw new ApiError({ status: 404, code: 'CASE_NOT_FOUND', reason: 'No such case', message: `No case in this tenant carries the reference ${id}.`, correlationId: null });
    const wire = await request<{ id: string; mriVersion: number; note: string | null; revocationReason: string | null; revokedAt: string | null }>('POST', `${CASES}/clarificationCase/${caseId}/signOff`, {
      body,
      ifMatch: version,
    });
    return { id: wire.data.id, mriVersion: wire.data.mriVersion, note: wire.data.note, revokedAt: wire.data.revokedAt };
  },

  async listOfferings(): Promise<readonly Offering[]> {
    const [candidates, characteristics, specifications] = await Promise.all([
      loadCandidates(),
      loadCharacteristics(),
      loadSpecifications(),
    ]);
    return toOfferings(candidates, characteristics.wires, specifications);
  },

  async listCharacteristics(): Promise<readonly Characteristic[]> {
    return toCharacteristics((await loadCharacteristics()).wires);
  },

  async listCustomers(): Promise<readonly Customer[]> {
    const [customers, templates] = await Promise.all([loadCustomers(), loadTemplates()]);
    return toCustomers(customers, templatesByTenant(templates));
  },

  async listSites(): Promise<readonly Site[]> {
    const wires = await getCollection<WireSite>(`${CUSTOMERS}/geographicSite`, {
      query: { limit: PAGE },
    });
    return toSites(wires);
  },

  async createSite(draft: SiteDraft): Promise<Site> {
    const created = await request<WireSite>('POST', `${CUSTOMERS}/geographicSite`, {
      body: draft satisfies WireSiteCreate,
    });
    return toSite(created.data);
  },

  async createSites(drafts: readonly SiteDraft[]): Promise<readonly Site[]> {
    const body: WireSiteBulkCreate = {
      items: drafts.map(
        (draft) =>
          ({
            ...draft,
            place: { ...draft.place },
          }) satisfies WireSiteCreate,
      ),
    };
    const created = await request<WireSite[]>('POST', `${CUSTOMERS}/geographicSite/bulk`, {
      body,
    });
    return toSites(created.data);
  },

  async updateSite(id: string, patch: SitePatch, version: number): Promise<Site> {
    const updated = await request<WireSite>('PATCH', `${CUSTOMERS}/geographicSite/${id}`, {
      body: patch satisfies WireSitePatch,
      contentType: 'application/merge-patch+json',
      ifMatch: `"${version}"`,
    });
    return toSite(updated.data);
  },

  async listRequesterMasters(): Promise<readonly RequesterMaster[]> {
    const wires = await getCollection<WireRequester>(`${REQUESTERS}/requester`, {
      query: { limit: PAGE },
    });
    return toRequesterMasters(wires);
  },

  /**
   * Onboard a requester and the addresses that resolve to them, in one write.
   *
   * `toRequesterMasters` is reused on the single resource the POST answers with
   * rather than re-reading the list: the row the screen shows next is then the
   * row the service just made, at the version it made it at, and there is no
   * moment where the new requester is on screen without the version its first
   * retirement will have to name.
   */
  async createRequesterMaster(draft: RequesterDraft): Promise<RequesterMaster> {
    const body: WireRequesterCreate = {
      displayName: draft.displayName,
      emailAddress: draft.emails.map((email) => ({ email })),
      ...(draft.role ? { role: draft.role } : {}),
    };
    const created = await request<WireRequester>('POST', `${REQUESTERS}/requester`, { body });
    return toRequesterMaster(created.data);
  },

  /**
   * Retire, suspend or reinstate one requester. There is no delete.
   *
   * `If-Match` is not optional, and the version comes from the row the operator
   * was looking at. Re-reading the requester here to obtain a fresh version
   * would defeat the precondition entirely — it would turn "change the state I
   * saw" into "change whatever the state is now", which is the blind overwrite
   * the 428 exists to prevent.
   */
  async setRequesterMasterStatus(
    id: string,
    status: RequesterStatus,
    version: number,
  ): Promise<RequesterMaster> {
    const patched = await request<WireRequester>('PATCH', `${REQUESTERS}/requester/${id}`, {
      body: { status } satisfies WireRequesterStatusPatch,
      contentType: 'application/merge-patch+json',
      ifMatch: `"${version}"`,
    });
    return toRequesterMaster(patched.data);
  },

  /**
   * What has been cited on the cases opened so far.
   *
   * Not a fetch. Provenance is per case — there is no collection of it across a
   * tenant, and building one would mean reading every case's values to answer a
   * question about the one on screen — so this returns the register that
   * `getDossier()` fills, after waiting for any dossier still in flight.
   *
   * Empty before a case has been opened, which is correct: nothing has been
   * cited yet, and the panel says so.
   */
  async getEvidence(): Promise<Readonly<Record<string, EvidenceItem>>> {
    // Settled, not resolved: a dossier that failed still means "no longer about
    // to write", and waiting for it to succeed would hang on a case that never
    // will.
    await Promise.allSettled([...gathering]);
    return Object.fromEntries(register);
  },

  async listThreads(): Promise<Readonly<Record<string, readonly ThreadMessage[]>>> {
    const index = await loadCases();
    const [items, drafts, customers] = await Promise.all([
      getCollection<WireCaseItem>(`${CASES}/caseItem`, { query: { limit: PAGE } }),
      loadClarifications(index.wires.slice(0, THREAD_CASES).map((wire) => wire.id)),
      customerNames(),
    ]);
    return toThreads(items, drafts, index.referenceOf, {
      cases: index.byId,
      customerNames: customers,
    });
  },

  async listClassificationRules(): Promise<readonly ClassificationRule[]> {
    return toClassificationRules(await loadRules());
  },

  async listBusinessRules(): Promise<readonly BusinessRule[]> {
    return toBusinessRules(await loadRules());
  },

  async listThresholds(): Promise<readonly Threshold[]> {
    return toThresholds(activeThreshold(await loadThresholds()));
  },

  async listTemplates(): Promise<readonly ClarificationTemplate[]> {
    const [templates, customers] = await Promise.all([loadTemplates(), customerNames()]);
    return toTemplates(templates, customers);
  },

  /**
   * Empty, because nothing stores one.
   *
   * The question bank is standard phrasing per characteristic and register, and
   * there is no table behind it — the configuration slice publishes business
   * rules, templates, thresholds and retention policies, and this is not among
   * them. Returning `{}` is the honest answer: the screens fall back to the
   * template's own wording, which is what actually gets sent.
   *
   * When a table lands, this becomes a read and nothing above it changes.
   */
  async getQuestionBank(): Promise<QuestionBank> {
    return {};
  },

  async getSettings(): Promise<CockpitSettings> {
    return toCockpitSettings(await get<WireSettings>(`${COCKPIT}/settings`));
  },

  /* ---------- tenancy ---------- */

  /* Not coalesced, and none of the five below are.
   *
   * `coalesce` exists for reads a dozen screens make on mount. These are made by
   * two screens, one of which is the tenant picker — and the picker is precisely
   * the place where a five-second window is wrong: an operator who was just
   * granted a tenant and hits reload must see it, not the answer from before the
   * grant. React Query's `staleTime` still applies above; that is a cache
   * somebody can reason about, which is the difference.
   */

  async listTenantChoices(query: ListQuery = {}): Promise<TenantChoices> {
    const page = await request<WireTenantChoices>('GET', `${TENANCY}/tenants`, {
      query: { name: query.search, offset: query.offset, limit: query.limit },
    });
    const wire = page.data;
    const tenants = (wire.tenants ?? []).map((tenant) => ({
      id: tenant.id,
      name: tenant.name,
      status: tenant.status,
    }));
    return {
      tenants,
      allTenants: wire.allTenants,
      selectedId: wire.selectedId ?? null,
      /* Falling back to the page's own length rather than to zero. A missing
         count is a service that did not send the header, and reporting nought
         tenants when several just arrived would be the one reading a screen
         cannot recover from. */
      total: page.total ?? tenants.length,
    };
  },

  async listTenantCustomers(query: ListQuery = {}): Promise<PageOf<TenantCustomer>> {
    const page = await request<WireTenantCustomer[]>('GET', `${TENANCY}/customers`, {
      query: { name: query.search, offset: query.offset, limit: query.limit },
    });
    const rows = page.data.map(toTenantCustomer);
    return { rows, total: page.total ?? rows.length };
  },

  async getTenantCustomerMasterData(): Promise<TenantCustomerMasterData> {
    const wire = await get<WireTenantCustomerMasterData>(`${TENANCY}/customer-master-data`);
    return {
      countries: wire.countries.map(({ code, name }) => ({ code, name })),
      languages: wire.languages.map(({ code, name }) => ({ code, name })),
    };
  },

  async createTenantCustomer(draft: TenantCustomerDraft): Promise<TenantCustomer> {
    const body: WireCustomerCreate = {
      name: draft.name,
      /* Spread rather than sent as null. An absent key means "the deployment's
         default"; an explicit null would be this screen asserting the tenant has
         no residency, which is a different claim and one nobody made. */
      ...(draft.countryCode ? { countryCode: draft.countryCode } : {}),
      ...(draft.defaultLanguage ? { defaultLanguage: draft.defaultLanguage } : {}),
    };
    const created = await request<WireTenantCustomer>('POST', `${TENANCY}/customers`, { body });
    return toTenantCustomer(created.data);
  },

  async createTenantCustomers(
    drafts: readonly TenantCustomerDraft[],
  ): Promise<readonly TenantCustomer[]> {
    const body: WireCustomerBulkCreate = {
      items: drafts.map((draft) => ({
        name: draft.name,
        ...(draft.countryCode ? { countryCode: draft.countryCode } : {}),
        ...(draft.defaultLanguage ? { defaultLanguage: draft.defaultLanguage } : {}),
      })),
    };
    const created = await request<WireTenantCustomer[]>('POST', `${TENANCY}/customers/bulk`, {
      body,
    });
    return created.data.map(toTenantCustomer);
  },

  async updateTenantCustomer(
    id: string,
    patch: TenantCustomerPatch,
    version: number,
  ): Promise<TenantCustomer> {
    const body: WireCustomerPatch = {
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      ...(patch.countryCode !== undefined ? { countryCode: patch.countryCode } : {}),
      ...(patch.defaultLanguage !== undefined ? { defaultLanguage: patch.defaultLanguage } : {}),
      ...(patch.status !== undefined ? { status: patch.status } : {}),
    };
    const patched = await request<WireTenantCustomer>('PATCH', `${TENANCY}/customers/${id}`, {
      body,
      contentType: 'application/merge-patch+json',
      ifMatch: `"${version}"`,
    });
    return toTenantCustomer(patched.data);
  },

  async getCatalogOffering(id: string): Promise<CatalogOfferingAdmin> {
    const result = await request<WireCatalogOffering>('GET', `/catalog-admin/v1/offerings/${id}`);
    return toCatalogOffering(result.data);
  },

  async createCatalogOffering(draft: CatalogOfferingDraft): Promise<CatalogOfferingAdmin> {
    const result = await request<WireCatalogOffering>('POST', '/catalog-admin/v1/offerings', {
      body: toWireCatalogOffering(draft),
    });
    return toCatalogOffering(result.data);
  },

  async createCatalogOfferings(
    drafts: readonly CatalogOfferingDraft[],
  ): Promise<readonly CatalogOfferingAdmin[]> {
    const result = await request<WireCatalogOffering[]>('POST', '/catalog-admin/v1/offerings/bulk', {
      body: { items: drafts.map(toWireCatalogOffering) } satisfies Schemas['CatalogOfferingBulkCreate'],
    });
    return result.data.map(toCatalogOffering);
  },

  async importCatalogCsv(file: File): Promise<CsvImportResult> {
    const multipart = new FormData();
    multipart.set('file', file, file.name);
    const imported = await request<WireCsvImportResult>('POST', `${CATALOG_ADMIN}/csv-import`, {
      multipart,
    });
    return toCsvImportResult(imported.data);
  },

  async updateCatalogOffering(
    id: string,
    patch: CatalogOfferingPatch,
    version: number,
  ): Promise<CatalogOfferingAdmin> {
    const body: WireCatalogOfferingPatch = {
      ...(patch.catalogName === undefined ? {} : { catalogName: patch.catalogName }),
      ...(patch.catalogVersion === undefined ? {} : { catalogVersion: patch.catalogVersion }),
      ...(patch.catalogLifecycle === undefined
        ? {}
        : { catalogLifecycle: patch.catalogLifecycle }),
      ...(patch.specificationName === undefined
        ? {}
        : { specificationName: patch.specificationName }),
      ...(patch.specificationVersion === undefined
        ? {}
        : { specificationVersion: patch.specificationVersion }),
      ...(patch.specificationDescription === undefined
        ? {}
        : { specificationDescription: patch.specificationDescription }),
      ...(patch.isBundle === undefined ? {} : { isBundle: patch.isBundle }),
      ...(patch.candidateName === undefined ? {} : { candidateName: patch.candidateName }),
      ...(patch.category === undefined ? {} : { category: patch.category }),
      ...(patch.regions === undefined ? {} : { regions: [...patch.regions] }),
      ...(patch.lifecycleStatus === undefined
        ? {}
        : { lifecycleStatus: patch.lifecycleStatus }),
      ...(patch.characteristics === undefined
        ? {}
        : { characteristics: patch.characteristics.map(toWireCatalogCharacteristic) }),
    };
    const result = await request<WireCatalogOffering>('PATCH', `/catalog-admin/v1/offerings/${id}`, {
      body,
      contentType: 'application/merge-patch+json',
      ifMatch: `"${version}"`,
    });
    return toCatalogOffering(result.data);
  },

  async listGrantsForCustomer(
    customerId: string,
    query: GrantQuery = {},
  ): Promise<PageOf<AccessGrant>> {
    return grantPage({ customerId }, query);
  },

  async listGrantsForSubject(
    subject: string,
    query: GrantQuery = {},
  ): Promise<PageOf<AccessGrant>> {
    return grantPage({ subject }, query);
  },

  async grantAccess(subject: string, customerId: string): Promise<AccessGrant> {
    const created = await request<WireGrant>('POST', `${TENANCY}/grants`, {
      body: { subject, customerId } satisfies WireGrantCreate,
    });
    return toAccessGrant(created.data);
  },

  async withdrawAccess(grantId: string): Promise<AccessGrant> {
    /* The response body is the point of using `request` rather than firing and
       refetching: the withdrawal stamp comes back on the row, so the screen can
       show who withdrew it and when without a second round trip that might read
       a different row. */
    const withdrawn = await request<WireGrant>('DELETE', `${TENANCY}/grants/${grantId}`);
    return toAccessGrant(withdrawn.data);
  },

  /* ---------- tenant groups ---------- */

  async listTenantGroups(query: TenantGroupQuery = {}): Promise<PageOf<TenantGroup>> {
    const page = await request<WireTenantGroup[]>('GET', `${TENANCY}/tenant-groups`, {
      query: {
        name: query.search,
        lifecycleStatus: query.lifecycle,
        offset: query.offset,
        limit: query.limit,
      },
    });
    const rows = page.data.map(toTenantGroup);
    return { rows, total: page.total ?? rows.length };
  },

  async listTenantGroupCustomers(
    id: string,
    query: ListQuery = {},
  ): Promise<PageOf<TenantGroupAssignment>> {
    const page = await request<WireTenantCustomerAssignment[]>(
      'GET',
      `${TENANCY}/tenant-groups/${id}/customers`,
      { query: { offset: query.offset, limit: query.limit } },
    );
    const rows = page.data.map(toTenantGroupAssignment);
    return { rows, total: page.total ?? rows.length };
  },

  async listTenantGroupServices(
    id: string,
    query: ListQuery = {},
  ): Promise<PageOf<TenantGroupAssignment>> {
    const page = await request<WireTenantServiceAssignment[]>(
      'GET',
      `${TENANCY}/tenant-groups/${id}/services`,
      { query: { offset: query.offset, limit: query.limit } },
    );
    const rows = page.data.map(toTenantGroupAssignment);
    return { rows, total: page.total ?? rows.length };
  },

  async listTenantGroupServiceCandidates(
    query: ListQuery = {},
  ): Promise<PageOf<TenantGroupServiceCandidate>> {
    const page = await request<WireTenantGroupServiceCandidate[]>(
      'GET',
      `${TENANCY}/tenant-groups/service-candidates`,
      {
        query: {
          name: query.search,
          offset: query.offset,
          limit: query.limit,
        },
      },
    );
    const rows = page.data.map(toTenantGroupServiceCandidate);
    return { rows, total: page.total ?? rows.length };
  },

  async createTenantGroup(name: string): Promise<TenantGroup> {
    const body = { name } satisfies WireTenantGroupCreate;
    const created = await request<WireTenantGroup>('POST', `${TENANCY}/tenant-groups`, { body });
    return toTenantGroup(created.data);
  },

  async updateTenantGroup(
    id: string,
    patch: TenantGroupPatch,
    version: number,
  ): Promise<TenantGroup> {
    const body: WireTenantGroupPatch = {
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      ...(patch.lifecycle !== undefined ? { lifecycleStatus: patch.lifecycle } : {}),
    };
    const changed = await request<WireTenantGroup>('PATCH', `${TENANCY}/tenant-groups/${id}`, {
      body,
      contentType: 'application/merge-patch+json',
      ifMatch: `"${version}"`,
    });
    return toTenantGroup(changed.data);
  },

  async assignTenantGroupCustomers(
    id: string,
    customerIds: readonly string[],
  ): Promise<TenantGroup> {
    const body = { customerIds: [...customerIds] } satisfies WireCustomerAssignments;
    const changed = await request<WireTenantGroup>(
      'POST',
      `${TENANCY}/tenant-groups/${id}/customers`,
      { body },
    );
    return toTenantGroup(changed.data);
  },

  async unassignTenantGroupCustomer(id: string, customerId: string): Promise<void> {
    await request<void>('DELETE', `${TENANCY}/tenant-groups/${id}/customers/${customerId}`);
  },

  async assignTenantGroupServices(
    id: string,
    serviceIds: readonly string[],
  ): Promise<TenantGroup> {
    const body = { serviceIds: [...serviceIds] } satisfies WireServiceAssignments;
    const changed = await request<WireTenantGroup>(
      'POST',
      `${TENANCY}/tenant-groups/${id}/services`,
      { body },
    );
    return toTenantGroup(changed.data);
  },

  async unassignTenantGroupService(id: string, serviceId: string): Promise<void> {
    await request<void>('DELETE', `${TENANCY}/tenant-groups/${id}/services/${serviceId}`);
  },

  /* ---------- MRI field sets ---------- */

  async listMriFieldSets(): Promise<readonly MriFieldSetAdmin[]> {
    const result = await request<WireMriFieldSet[]>('GET', `${CONFIG_ADMIN}/mriFieldSet`);
    return result.data.map(toMriFieldSet);
  },

  async getMriFieldSet(setKind: MriFieldSetKind): Promise<MriFieldSetAdmin> {
    const result = await request<WireMriFieldSet>(
      'GET',
      `${CONFIG_ADMIN}/mriFieldSet/${setKind}`,
    );
    return toMriFieldSet(result.data);
  },

  async putMriFieldSet(
    setKind: MriFieldSetKind,
    draft: MriFieldSetDraft,
    version: number,
  ): Promise<MriFieldSetAdmin> {
    const result = await request<WireMriFieldSet>('PUT', `${CONFIG_ADMIN}/mriFieldSet/${setKind}`, {
      body: toWireMriFieldSetCreate(draft),
      ifMatch: `"${version}"`,
    });
    return toMriFieldSet(result.data);
  },

  async patchMriFieldSet(
    setKind: MriFieldSetKind,
    patch: MriFieldSetPatch,
    version: number,
  ): Promise<MriFieldSetAdmin> {
    const result = await request<WireMriFieldSet>(
      'PATCH',
      `${CONFIG_ADMIN}/mriFieldSet/${setKind}`,
      {
        body: toWireMriFieldSetPatch(patch),
        contentType: 'application/merge-patch+json',
        ifMatch: `"${version}"`,
      },
    );
    return toMriFieldSet(result.data);
  },

  async addMriField(setKind: MriFieldSetKind, field: MriFieldDraft): Promise<MriFieldSetAdmin> {
    const result = await request<WireMriFieldSet>(
      'POST',
      `${CONFIG_ADMIN}/mriFieldSet/${setKind}/field`,
      { body: toWireMriFieldCreate(field) },
    );
    return toMriFieldSet(result.data);
  },

  async removeMriField(setKind: MriFieldSetKind, fieldId: string): Promise<void> {
    await request<void>(
      'DELETE',
      `${CONFIG_ADMIN}/mriFieldSet/${setKind}/field/${fieldId}`,
    );
  },

  async listAuditEvents(id: string) {
    const index = await loadCases();
    const caseId = index.idOf.get(id) ?? id;
    const wires = await get<
      Array<{
        id: string;
        sequenceNo: number;
        eventType: string;
        entityType: string;
        actorId: string;
        actorType: string;
        actorRoles?: string[];
        action: string;
        outcome: string;
        occurredAt: string;
        recordedAt: string;
      }>
    >(`${CASES}/auditEvent`, { query: { 'case.id': caseId, limit: PAGE } });
    return wires.map((w) => ({
      id: w.id,
      sequenceNo: w.sequenceNo,
      eventType: w.eventType,
      entityType: w.entityType,
      actorId: w.actorId,
      actorType: w.actorType,
      actorRoles: w.actorRoles ?? [],
      action: w.action,
      outcome: w.outcome,
      occurredAt: w.occurredAt,
      recordedAt: w.recordedAt,
    }));
  },
};

/**
 * Empty the coalescer, the evidence register and the in-flight set.
 *
 * All three are process-lifetime state, which is right in a browser tab — the
 * only way to want them cleared there is to reload — and wrong in a test
 * process, where one file renders a dozen screens against a dozen different
 * responses. Without this the second render answers from the first one's
 * five-second window, and a test asserting an error state would quietly pass
 * against the success body its neighbour fetched.
 *
 * Nothing in the app calls it. It is exported so `tests/support/render.tsx` can,
 * which is a smaller compromise than making the three maps injectable and
 * threading a container through a module every screen reaches transitively.
 */
export function resetTransportState(): void {
  inflight.clear();
  register.clear();
  gathering.clear();
}
